import { useState, useMemo, useEffect } from 'react'
import { useNavigate, Navigate, Link, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import {
  useRegisterMutation,
  useLazyMeQuery,
  usePublicPlansQuery,
  usePublicIndustriesQuery,
  type PublicPlan,
  type PublicIndustry,
} from '@/features/auth/auth-api'
import { setCredentials, setIdentity } from '@/features/auth/auth-slice'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Logo } from '@/components/layout/logo'
import { formatPaisa } from '@/lib/format'
import type { ApiError } from '@/lib/types'

/**
 * SignupWizard — 4-step DistroOps signup flow:
 *
 *   Step 1: Pick industry (drives module preselection)
 *   Step 2: Pick plan (trial starts on selected tier)
 *   Step 3: Company details (name, subdomain, country, currency)
 *   Step 4: First admin user (your login)
 *
 * Final POST hits /v1/auth/register with all collected fields,
 * receives a JWT, and lands the new admin on /dashboard.
 */

type Step = 1 | 2 | 3 | 4

interface WizardState {
  industry_template_code: string
  plan_code: string
  billing_cycle: 'monthly' | 'annual'
  company_name: string
  subdomain: string
  country_code: string
  currency_code: string
  timezone: string
  first_name: string
  last_name: string
  email: string
  phone: string
  password: string
  password_confirmation: string
}

const INITIAL: WizardState = {
  industry_template_code: '',
  plan_code: '',
  billing_cycle: 'monthly',
  company_name: '',
  subdomain: '',
  country_code: 'PK',
  currency_code: 'PKR',
  timezone: 'Asia/Karachi',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  password: '',
  password_confirmation: '',
}

const COUNTRIES = [
  { code: 'PK', label: 'Pakistan', currency: 'PKR', timezone: 'Asia/Karachi' },
  { code: 'AE', label: 'United Arab Emirates', currency: 'AED', timezone: 'Asia/Dubai' },
  { code: 'SA', label: 'Saudi Arabia', currency: 'SAR', timezone: 'Asia/Riyadh' },
  { code: 'AU', label: 'Australia', currency: 'AUD', timezone: 'Australia/Sydney' },
]

export function SignupWizard() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated)

  const [step, setStep] = useState<Step>(1)
  const [state, setState] = useState<WizardState>(INITIAL)
  const [register, { isLoading: registering }] = useRegisterMutation()
  const [fetchMe] = useLazyMeQuery()

  const { data: industriesData } = usePublicIndustriesQuery()
  const { data: plansData } = usePublicPlansQuery()

  // Read plan from URL — marketing site pricing CTAs link with ?plan=growth&cycle=monthly
  const [searchParams] = useSearchParams()
  useEffect(() => {
    const planFromUrl = searchParams.get('plan')
    const cycleFromUrl = searchParams.get('cycle')
    if (planFromUrl && plansData?.data?.some((p) => p.code === planFromUrl)) {
      setState((prev) => ({
        ...prev,
        plan_code: planFromUrl,
        billing_cycle: cycleFromUrl === 'annual' ? 'annual' : 'monthly',
      }))
    }
  }, [searchParams, plansData])

  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  const update = <K extends keyof WizardState>(key: K, value: WizardState[K]) => {
    setState((prev) => ({ ...prev, [key]: value }))
  }

  const updateCountry = (code: string) => {
    const c = COUNTRIES.find((x) => x.code === code)
    setState((prev) => ({
      ...prev,
      country_code: code,
      currency_code: c?.currency ?? prev.currency_code,
      timezone: c?.timezone ?? prev.timezone,
    }))
  }

  const canAdvance = useMemo(() => {
    if (step === 1) return state.industry_template_code !== ''
    if (step === 2) return state.plan_code !== ''
    if (step === 3) {
      return state.company_name.trim() !== '' &&
        /^[a-z0-9-]{3,50}$/.test(state.subdomain) &&
        state.country_code !== ''
    }
    if (step === 4) {
      return state.first_name.trim() !== '' &&
        state.last_name.trim() !== '' &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email) &&
        state.password.length >= 8 &&
        state.password === state.password_confirmation
    }
    return false
  }, [step, state])

  const submit = async () => {
    try {
      const res = await register({
        company_name: state.company_name,
        subdomain: state.subdomain,
        first_name: state.first_name,
        last_name: state.last_name,
        email: state.email,
        phone: state.phone || undefined,
        password: state.password,
        password_confirmation: state.password_confirmation,
        country_code: state.country_code,
        timezone: state.timezone,
        currency_code: state.currency_code,
        industry_template_code: state.industry_template_code,
        plan_code: state.plan_code,
      }).unwrap()

      dispatch(setCredentials({ access_token: res.access_token }))
      const me = await fetchMe().unwrap()
      dispatch(setIdentity({ user: me.user, company: me.company }))
      toast.success(`Welcome to DistroOps, ${me.user.full_name}!`)
      navigate('/', { replace: true })
    } catch (err) {
      const apiErr = (err as { data?: ApiError })?.data
      toast.error(apiErr?.message ?? 'Sign-up failed. Please check the form.')
    }
  }

  return (
    <div className="min-h-screen bg-brand-ink px-4 py-10">
      <div className="mx-auto max-w-3xl">
        <div className="mb-8 flex justify-center">
          <Logo className="[&_span]:text-brand-cream" />
        </div>

        <Card>
          <CardContent className="pt-6">
            <StepIndicator step={step} />

            {step === 1 && (
              <IndustryStep
                industries={industriesData?.data ?? []}
                selected={state.industry_template_code}
                onSelect={(code) => update('industry_template_code', code)}
              />
            )}

            {step === 2 && (
              <PlanStep
                plans={plansData?.data ?? []}
                selected={state.plan_code}
                billingCycle={state.billing_cycle}
                onSelectPlan={(code) => update('plan_code', code)}
                onBillingCycleChange={(c) => update('billing_cycle', c)}
              />
            )}

            {step === 3 && (
              <CompanyDetailsStep state={state} update={update} updateCountry={updateCountry} />
            )}

            {step === 4 && (
              <AdminAccountStep state={state} update={update} />
            )}

            <div className="mt-8 flex items-center justify-between">
              {step > 1 ? (
                <Button variant="outline" onClick={() => setStep((step - 1) as Step)}>
                  Back
                </Button>
              ) : (
                <Link to="/login" className="text-sm text-blue-700 hover:underline">
                  Already have an account? Sign in
                </Link>
              )}
              {step < 4 ? (
                <Button
                  disabled={!canAdvance}
                  onClick={() => setStep((step + 1) as Step)}
                >
                  Continue
                </Button>
              ) : (
                <Button
                  disabled={!canAdvance || registering}
                  onClick={submit}
                >
                  {registering ? 'Creating...' : 'Create account & start trial'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function StepIndicator({ step }: { step: Step }) {
  const labels = ['Industry', 'Plan', 'Company', 'Account']
  return (
    <div className="mb-6 flex items-center justify-between">
      {labels.map((label, i) => {
        const n = (i + 1) as Step
        const active = step === n
        const completed = step > n
        return (
          <div key={label} className="flex items-center">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${
                completed ? 'bg-green-600 text-white' :
                active ? 'bg-blue-600 text-white' :
                'bg-gray-200 text-gray-600'
              }`}
            >
              {completed ? '✓' : n}
            </div>
            <span className={`ml-2 text-sm ${active ? 'font-medium' : 'text-gray-500'}`}>{label}</span>
            {n < 4 && <div className={`mx-3 h-px w-12 ${completed ? 'bg-green-600' : 'bg-gray-300'}`} />}
          </div>
        )
      })}
    </div>
  )
}

function IndustryStep({
  industries, selected, onSelect,
}: {
  industries: PublicIndustry[]
  selected: string
  onSelect: (code: string) => void
}) {
  const grouped = useMemo(() => {
    const out: Record<string, PublicIndustry[]> = {}
    industries.forEach((i) => {
      const c = i.category || 'other'
      out[c] = out[c] ?? []
      out[c].push(i)
    })
    return out
  }, [industries])

  return (
    <div>
      <h2 className="font-display text-xl font-semibold">What industry are you in?</h2>
      <p className="mt-1 mb-5 text-sm text-gray-600">
        This pre-selects the modules that match your business — you can change them later.
      </p>

      <div className="space-y-5">
        {Object.entries(grouped).map(([category, list]) => (
          <div key={category}>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
              {category.replace(/_/g, ' ')}
            </div>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
              {list.map((ind) => {
                const active = selected === ind.code
                return (
                  <button
                    key={ind.code}
                    type="button"
                    onClick={() => onSelect(ind.code)}
                    className={`rounded-lg border p-3 text-left text-sm transition ${
                      active
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500'
                        : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    <div className="font-medium">{ind.name}</div>
                    {ind.description && (
                      <div className="mt-0.5 text-xs text-gray-500 line-clamp-2">
                        {ind.description}
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function PlanStep({
  plans, selected, billingCycle, onSelectPlan, onBillingCycleChange,
}: {
  plans: PublicPlan[]
  selected: string
  billingCycle: 'monthly' | 'annual'
  onSelectPlan: (code: string) => void
  onBillingCycleChange: (c: 'monthly' | 'annual') => void
}) {
  return (
    <div>
      <h2 className="font-display text-xl font-semibold">Choose a plan</h2>
      <p className="mt-1 mb-4 text-sm text-gray-600">
        Start with a 14-day free trial. Upgrade, downgrade, or cancel anytime.
      </p>

      <div className="mb-4 flex justify-center">
        <div className="inline-flex rounded-lg border bg-gray-50 p-0.5 text-sm">
          <button
            type="button"
            onClick={() => onBillingCycleChange('monthly')}
            className={`rounded-md px-3 py-1 ${billingCycle === 'monthly' ? 'bg-white shadow' : 'text-gray-600'}`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => onBillingCycleChange('annual')}
            className={`rounded-md px-3 py-1 ${billingCycle === 'annual' ? 'bg-white shadow' : 'text-gray-600'}`}
          >
            Annual <Badge className="ml-1 bg-green-100 text-green-800">save ~17%</Badge>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
        {plans.map((plan) => {
          const active = selected === plan.code
          const price = billingCycle === 'monthly' ? plan.monthly_price : Math.round(plan.annual_price / 12)
          return (
            <button
              key={plan.code}
              type="button"
              onClick={() => onSelectPlan(plan.code)}
              className={`rounded-lg border p-4 text-left transition ${
                active ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500' : 'border-gray-200 hover:border-gray-400'
              }`}
            >
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                {plan.tier}
              </div>
              <div className="mt-1 text-lg font-semibold">{plan.name}</div>
              <div className="mt-2 text-2xl font-bold">
                {formatPaisa(price, plan.currency_code)}
                <span className="text-sm font-normal text-gray-500">/mo</span>
              </div>
              {billingCycle === 'annual' && (
                <div className="text-xs text-gray-500">
                  billed annually: {formatPaisa(plan.annual_price, plan.currency_code)}
                </div>
              )}
              <ul className="mt-3 space-y-1 text-xs text-gray-600">
                {plan.max_users !== null && (
                  <li>• Up to {plan.max_users} users</li>
                )}
                {plan.max_users === null && (
                  <li>• Unlimited users</li>
                )}
                {plan.max_customers !== null && (
                  <li>• Up to {plan.max_customers.toLocaleString()} customers</li>
                )}
                {(plan.features ?? []).slice(0, 3).map((f) => (
                  <li key={f}>• {f}</li>
                ))}
              </ul>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function CompanyDetailsStep({
  state, update, updateCountry,
}: {
  state: WizardState
  update: <K extends keyof WizardState>(key: K, value: WizardState[K]) => void
  updateCountry: (code: string) => void
}) {
  return (
    <div>
      <h2 className="font-display text-xl font-semibold">Tell us about your company</h2>
      <p className="mt-1 mb-5 text-sm text-gray-600">
        This is the information we'll use for invoicing and tenant identification.
      </p>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="md:col-span-2 space-y-1">
          <Label>Company name</Label>
          <Input
            value={state.company_name}
            onChange={(e) => update('company_name', e.target.value)}
            placeholder="Acme Distribution Pvt Ltd"
          />
        </div>

        <div className="md:col-span-2 space-y-1">
          <Label>Subdomain</Label>
          <div className="flex">
            <Input
              value={state.subdomain}
              onChange={(e) => update('subdomain', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              placeholder="acme"
              className="rounded-r-none"
            />
            <div className="flex items-center rounded-r-md border border-l-0 bg-gray-50 px-3 text-sm text-gray-500">
              .distroops.app
            </div>
          </div>
          <p className="text-xs text-gray-500">Lowercase, numbers, hyphens only — 3 to 50 chars.</p>
        </div>

        <div className="space-y-1">
          <Label>Country</Label>
          <select
            className="h-10 w-full rounded-md border border-gray-300 px-3 text-sm"
            value={state.country_code}
            onChange={(e) => updateCountry(e.target.value)}
          >
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>{c.label}</option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <Label>Currency</Label>
          <Input
            value={state.currency_code}
            onChange={(e) => update('currency_code', e.target.value.toUpperCase())}
            maxLength={3}
          />
        </div>
      </div>
    </div>
  )
}

function AdminAccountStep({
  state, update,
}: {
  state: WizardState
  update: <K extends keyof WizardState>(key: K, value: WizardState[K]) => void
}) {
  return (
    <div>
      <h2 className="font-display text-xl font-semibold">Create your admin account</h2>
      <p className="mt-1 mb-5 text-sm text-gray-600">
        This will be the first user — you can invite teammates after signup.
      </p>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-1">
          <Label>First name</Label>
          <Input
            value={state.first_name}
            onChange={(e) => update('first_name', e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label>Last name</Label>
          <Input
            value={state.last_name}
            onChange={(e) => update('last_name', e.target.value)}
          />
        </div>
        <div className="md:col-span-2 space-y-1">
          <Label>Work email</Label>
          <Input
            type="email"
            value={state.email}
            onChange={(e) => update('email', e.target.value)}
          />
        </div>
        <div className="md:col-span-2 space-y-1">
          <Label>Phone (optional)</Label>
          <Input
            value={state.phone}
            onChange={(e) => update('phone', e.target.value)}
            placeholder="+92 300 1234567"
          />
        </div>
        <div className="space-y-1">
          <Label>Password</Label>
          <Input
            type="password"
            value={state.password}
            onChange={(e) => update('password', e.target.value)}
          />
          <p className="text-xs text-gray-500">Min 8 chars, mixed case, numbers, symbols.</p>
        </div>
        <div className="space-y-1">
          <Label>Confirm password</Label>
          <Input
            type="password"
            value={state.password_confirmation}
            onChange={(e) => update('password_confirmation', e.target.value)}
          />
          {state.password_confirmation !== '' &&
           state.password !== state.password_confirmation && (
            <p className="text-xs text-red-600">Passwords don't match.</p>
          )}
        </div>
      </div>
    </div>
  )
}
